import { useCallback, useEffect, useRef } from 'react'

import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { paymentCancelledEvent } from 'src/Helpers/TagManager'
import { getUserSelector } from 'src/Redux/auth-process/userSlice/selectors'
import { getPaymentMethod } from 'src/Redux/payment-process/selectors'
import { getAllCampaignsCount, useMetadataQuery } from 'src/Redux/metadataApi'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { usePaymentMethod } from 'src/Helpers/usePaymentMethod'
import { useLazyGetSpotifyCampaignStatusQuery } from 'src/Redux/spotifyCampaignStatusApi'

import { useResetQueryParams } from '../Helpers/useResetQueryParams'

import { useCustomSearchParams } from './useCustomSearchParams'

interface PaymentCancelledEvents {
  handlePaymentCancelledSpotifyEvent: (params: { platformName: string }) => void
}

export const usePaymentCancelledSpotifyEvents = (): PaymentCancelledEvents => {
  const { data } = useMetadataQuery(null)
  const param = useParams()
  const campaignId = Number(param.number) || 0
  const user = useSelector(getUserSelector)
  const paymentMethod = useSelector(getPaymentMethod)
  const amountOfCampaigns = getAllCampaignsCount(data)
  const { search } = useCustomSearchParams()
  const paymentCancelled = search.get('payment')
  const priceFromQuery = Number(search.get('price'))
  const isPayPalToken = search.get('token')
  const isPayPalSaved = search.get('isPayPalSaved')

  const initialized = useRef(false)

  const [getSpotifyCampaignStatus] = useLazyGetSpotifyCampaignStatusQuery()

  const resetQueryParams = useResetQueryParams([
    'payment',
    'token',
    'price',
    'isPayPalSaved',
  ])

  const paymentMethodFromFunction = usePaymentMethod(
    isPayPalToken,
    isPayPalSaved,
    paymentMethod,
  )

  const handlePaymentCancelledSpotifyEvent = useCallback(
    async (params: { platformName: string }): Promise<void> => {
      const campaignData = await getSpotifyCampaignStatus(campaignId)
        .unwrap()
        .catch(() => null)

      if (!campaignData) {
        return
      }

      paymentCancelledEvent({
        campaignId,
        paymentMethod: paymentMethodFromFunction,
        genresList:
          campaignData.track?.genres
            ?.map((genre) => genre.genreName)
            .join(', ') || '',
        userId: user.id,
        userRole: user.role,
        campaignNumber: amountOfCampaigns,
        platformName: params.platformName,
        campaignType: campaignData.type,
        campaignValue: campaignData.amount ?? priceFromQuery ?? 0,
        paymentAmount: campaignData.amount ?? priceFromQuery ?? 0,
      })

      resetQueryParams()
    },
    [
      getSpotifyCampaignStatus,
      campaignId,
      user.id,
      user.role,
      amountOfCampaigns,
      priceFromQuery,
      resetQueryParams,
      paymentMethodFromFunction,
    ],
  )

  useEffect(() => {
    if (paymentCancelled === 'cancelled' && !initialized.current) {
      initialized.current = true

      void handlePaymentCancelledSpotifyEvent({
        platformName: CampaignPlatformTypeEnum.SPOTIFY,
      })
    }
  }, [handlePaymentCancelledSpotifyEvent, paymentCancelled])

  return {
    handlePaymentCancelledSpotifyEvent,
  }
}
